import React, {useState} from 'react';
import {Box, IconButton, Menu, MenuItem, Select} from '@mui/material';
import {Language, Person, ExitToApp, Search, Brightness4Outlined, LightModeOutlined} from '@mui/icons-material';
import {useTranslate, useLocaleState, useLogout, useGetIdentity} from 'react-admin';
import {useNavigate} from 'react-router-dom';
import {t} from '../../themes/statamicTokens';
import {useChromeMode} from '../../themes/ThemeModeContext';

const LANGUAGES = [
    {code: 'en', label: 'English'},
    {code: 'nl', label: 'Nederlands'},
    {code: 'ru', label: 'Русский'},
    {code: 'hy', label: 'Հայերեն'},
    {code: 'de', label: 'Deutsch'},
    {code: 'pt', label: 'Português'},
];

const initialsFrom = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
};

const UserMenu = () => {
    const [anchor, setAnchor] = useState(null);
    const {identity} = useGetIdentity();
    const logout = useLogout();
    const navigate = useNavigate();
    const translate = useTranslate();

    const name = identity?.fullName ?? localStorage.getItem('username') ?? 'User';

    return (
        <>
            <Box
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    backgroundColor: t.color.accent.primary,
                    color: t.color.accent.onPrimary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                {initialsFrom(name)}
            </Box>
            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            minWidth: 200,
                            borderRadius: `${t.radius.md}px`,
                            border: `1px solid ${t.color.border.default}`,
                            boxShadow: t.shadow.dropdown,
                        },
                    },
                }}
            >
                <Box sx={{px: 2, py: 1.25, borderBottom: `1px solid ${t.color.border.hairline}`}}>
                    <Box sx={{fontSize: 13, fontWeight: 600, color: t.color.text.primary}}>{name}</Box>
                </Box>
                <MenuItem
                    onClick={() => { setAnchor(null); navigate('/profile'); }}
                    sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.primary}}
                >
                    <Person sx={{fontSize: 16, mr: 1.25, color: t.color.text.muted}}/>
                    {translate('My Profile')}
                </MenuItem>
                <MenuItem
                    onClick={() => { setAnchor(null); logout(); }}
                    sx={{fontSize: `${t.type.small.size}px`, color: t.color.status.dangerSolid}}
                >
                    <ExitToApp sx={{fontSize: 16, mr: 1.25}}/>
                    {translate('Logout')}
                </MenuItem>
            </Menu>
        </>
    );
};

const LocaleSwitcher = () => {
    const [locale, setLocale] = useLocaleState();
    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
            <Language sx={{fontSize: 16, color: t.color.text.muted}}/>
            <Select
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                variant="standard"
                disableUnderline
                sx={{
                    fontSize: `${t.type.small.size}px`,
                    color: t.color.text.secondary,
                    '& .MuiSelect-select': { py: 0, pr: '18px !important' },
                    '& .MuiSelect-icon': { color: t.color.text.muted },
                }}
            >
                {LANGUAGES.map(l => (
                    <MenuItem key={l.code} value={l.code} sx={{fontSize: `${t.type.small.size}px`}}>
                        {l.label}
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
};

const ChromeModeToggle = () => {
    const {mode, toggleChromeMode} = useChromeMode();
    const translate = useTranslate();
    return (
        <IconButton
            size="small"
            onClick={toggleChromeMode}
            title={mode === 'dark' ? translate('Switch to light sidebar') : translate('Switch to dark sidebar')}
            sx={{color: t.color.text.muted, '&:hover': {color: t.color.text.primary}}}
        >
            {mode === 'dark'
                ? <LightModeOutlined sx={{fontSize: 18}}/>
                : <Brightness4Outlined sx={{fontSize: 18}}/>}
        </IconButton>
    );
};

const StatamicAppBar = () => {
    const {chrome} = useChromeMode();
    const translate = useTranslate();
    return (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            height: 52,
            px: 2,
            gap: 2,
            backgroundColor: chrome.appBarBg,
            borderBottom: `1px solid ${chrome.appBarBorder}`,
            flexShrink: 0,
            zIndex: 1,
        }}
    >
        <Box
            role="button"
            tabIndex={0}
            onClick={() => window.dispatchEvent(new CustomEvent('open-command-palette'))}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    window.dispatchEvent(new CustomEvent('open-command-palette'));
                }
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                flex: '0 1 480px',
                height: 32,
                px: 1.25,
                borderRadius: `${t.radius.sm}px`,
                border: `1px solid ${t.color.border.default}`,
                backgroundColor: t.color.surface,
                cursor: 'pointer',
                '&:hover': { borderColor: t.color.border.strong },
                '&:focus': {
                    outline: 'none',
                    borderColor: t.color.accent.primary,
                    boxShadow: `0 0 0 3px ${t.color.accent.primaryTint}`,
                },
            }}
        >
            <Search sx={{fontSize: 16, color: t.color.text.muted, mr: 1}}/>
            <Box sx={{flex: 1, fontSize: `${t.type.small.size}px`, color: t.color.text.muted}}>
                {translate('Search...')}
            </Box>
            <Box
                component="kbd"
                sx={{
                    px: 0.75,
                    py: 0.125,
                    fontSize: 11,
                    fontFamily: t.font.mono,
                    color: t.color.text.muted,
                    border: `1px solid ${t.color.border.default}`,
                    borderRadius: `${t.radius.sm}px`,
                    backgroundColor: t.color.chip.monoBg,
                }}
            >
                /
            </Box>
        </Box>

        <Box sx={{flex: 1}}/>

        <ChromeModeToggle/>

        <LocaleSwitcher/>

        <UserMenu/>
    </Box>
    );
};

export default StatamicAppBar;
